import { useMutation, useQueryClient } from '@tanstack/react-query';
import ContentService from '@/services/ContentService';
import type { CustomContent } from '@/services/ContentService';
import { TASK_KEYS } from '@/state/data/task/keys';
import {
  patchArrayCaches, rollbackSnapshot, scheduleInvalidate,
} from '@/state/data/_shared/wf-race';

// Async writes via Tier 4 → n8n workflow. See _shared/wf-race.ts.

type ContentTask = {
  task_id?: string | number;
  status?: string;
  available_at?: string;
  due_at?: string;
  [key: string]: unknown;
};

export function useScheduleContentTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      withingsUserId: string | number;
      catalogId: string;
      scheduledDate: string;
      priority?: string;
    }) => ContentService.scheduleContentTask(
      vars.withingsUserId,
      vars.catalogId,
      vars.scheduledDate,
      vars.priority,
    ),
    onSettled: (_d, _e, vars) => scheduleInvalidate(qc, TASK_KEYS.contentForPatient(vars.withingsUserId)),
  });
}

export function useScheduleCustomContentTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      withingsUserId: string | number;
      customContent: CustomContent;
      scheduledDate: string;
      priority?: string;
    }) => ContentService.scheduleCustomContentTask(
      vars.withingsUserId,
      vars.customContent,
      vars.scheduledDate,
      vars.priority,
    ),
    onSettled: (_d, _e, vars) => scheduleInvalidate(qc, TASK_KEYS.contentForPatient(vars.withingsUserId)),
  });
}

export function useCancelContentTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { taskId: string | number; withingsUserId: string | number }) => (
      ContentService.cancelContentTask(vars.taskId, vars.withingsUserId)
    ),
    onMutate: ({ taskId }) => {
      const previous = patchArrayCaches<ContentTask>(qc, TASK_KEYS.all, (t) => (
        String(t.task_id) === String(taskId) ? { ...t, status: 'cancelled' } : t
      ));
      return { previous };
    },
    onError: (_e, _v, ctx) => rollbackSnapshot(qc, ctx?.previous),
    onSettled: () => scheduleInvalidate(qc, TASK_KEYS.all),
  });
}

export function useReactivateContentTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { taskId: string | number; withingsUserId: string | number }) => (
      ContentService.reactivateContentTask(vars.taskId, vars.withingsUserId)
    ),
    onMutate: ({ taskId }) => {
      const previous = patchArrayCaches<ContentTask>(qc, TASK_KEYS.all, (t) => (
        String(t.task_id) === String(taskId) ? { ...t, status: 'scheduled' } : t
      ));
      return { previous };
    },
    onError: (_e, _v, ctx) => rollbackSnapshot(qc, ctx?.previous),
    onSettled: () => scheduleInvalidate(qc, TASK_KEYS.all),
  });
}

export function useRescheduleContentTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { taskId: string | number; newDate: string; withingsUserId: string | number }) => (
      ContentService.rescheduleContentTask(vars.taskId, vars.newDate, vars.withingsUserId)
    ),
    onMutate: ({ taskId, newDate }) => {
      const previous = patchArrayCaches<ContentTask>(qc, TASK_KEYS.all, (t) => (
        String(t.task_id) === String(taskId)
          ? { ...t, available_at: `${newDate}T09:00:00Z` }
          : t
      ));
      return { previous };
    },
    onError: (_e, _v, ctx) => rollbackSnapshot(qc, ctx?.previous),
    onSettled: () => scheduleInvalidate(qc, TASK_KEYS.all),
  });
}
